<template>
    <div>
        <org-unit-users-controls
            v-model="fetchParams"
            @apply="applyFilters"
            @reset="resetFilters"
            :sortOptions="[
                {
                    value: 'added_at',
                    label: 'Sort: Added at',
                },
            ]"
            :roleOptions="orgUnitRolesOptions()"
            :orgUnitId="props.orgUnitId"
            :loading="loading"
            @add="openUserOrgUnitModal()"
        >
        </org-unit-users-controls>

        <page-loading v-if="loading && !list.length"></page-loading>

        <template v-if="!loading && !list.length">
            <empty-state v-if="!isEqual(defaultFilters(), fetchParams)" @reset="resetFilters" />
            <empty-state v-else-if="!list.length" class="mb-8">
                No users yet.
                <vx-button
                    v-if="onCorporate() || ['admin'].includes(roleOnCurrentOrgUnit)"
                    @click="openUserOrgUnitModal()"
                >
                    Add User <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </empty-state>
        </template>

        <vx-table v-if="list.length" class="xl:grid-cols-4 mt-0" rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div class="xl:col-span-2">Name</div>
                <div>Role</div>
            </vx-table-row>

            <org-unit-active-user-row
                v-for="(item, index) in list"
                v-model="list[index]"
                :key="list[index].id"
                :orgUnitId="props.orgUnitId"
                :teamId="props.teamId"
                :requireSso="props.requireSso"
                @edit="openUserOrgUnitModal(list[index])"
                @deleted="removeFromList(list[index].id)"
            >
            </org-unit-active-user-row>
        </vx-table>

        <div v-if="list.length" class="mt-8 flex flex-col w-full gap-10">
            <div class="flex justify-between items-center">
                <vx-button
                    v-if="fetchParams.cursor"
                    @click="loadData()"
                    color="muted-dark"
                    size="xl"
                    data-test="load-more-btn"
                    class="grow-0"
                    :loading="loading"
                >
                    Load More
                </vx-button>
                <div class="text-lg grow-0 ml-auto font-normal text-slate-600">{{ list.length }}/{{ totalCount }}</div>
            </div>
        </div>

        <vx-alert shadow class="mt-8" color="slate" :closeable="false">
            <template v-slot:icon><font-awesome-icon widthAuto :icon="faCircleInfo" /></template>
            Corporate or OU administrators with implicit access will not be duplicated here.
        </vx-alert>

        <org-unit-user-modal-form
            v-model="selectedUser"
            v-model:visible="showUserOrgUnitModal"
            :orgUnitId="props.orgUnitId"
            :orgUnitName="props.orgUnitName"
            :teamId="props.teamId"
            @added="userAdded($event)"
            @updated="userUpdated($event)"
            @invited="emit('invited')"
        ></org-unit-user-modal-form>
    </div>
</template>
<script setup>
import PageLoading from '~/components/general/PageLoading.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import OrgUnitActiveUserRow from './OrgUnitActiveUserRow.vue';
import OrgUnitUsersControls from './OrgUnitUsersControls.vue';
import { orgUnitUsersClient } from '~/services';
import { VxAlert, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { faCirclePlus, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { onMounted, ref } from 'vue';
import { isEqual } from '~/utils/helpers';
import { DEFAULT_ORG_UNIT_ROLE, orgUnitRolesOptions } from '~/constants/roles';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import OrgUnitUserModalForm from '~/components/settings/teams/org-units/form/users/OrgUnitUserModalForm.vue';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    orgUnitId: {
        type: Number,
        required: true,
    },
    orgUnitName: {
        type: String,
        required: false,
    },
    requireSso: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(['add', 'added', 'edit', 'updated', 'invited']);

const defaultFilters = () => ({
    cursor: null,
    sort_dir: 'desc',
    sort_field: 'added_at',
    per_page: 10,
    role: null,
    query: null,
});

const totalCount = ref(0);
const showUserOrgUnitModal = ref(false);
const selectedUser = ref({
    id: null,
    name: '',
    email: '',
    role: DEFAULT_ORG_UNIT_ROLE,
});
const openUserOrgUnitModal = (user) => {
    if (user) {
        selectedUser.value = { ...user };
    }

    showUserOrgUnitModal.value = true;
};

const { onCorporate, roleOnCurrentOrgUnit } = useCurrentGroup();
const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };

    loadData();
};

const loadData = () => {
    fetchRecords(orgUnitUsersClient.listOrgUnitUsers(props.teamId, props.orgUnitId, { ...fetchParams.value }));
    fetchTotalCount();
};

const fetchTotalCount = async () => {
    try {
        const response = await orgUnitUsersClient.countOrgUnitUsers(props.teamId, props.orgUnitId, {
            ...fetchParams.value,
        });
        totalCount.value = response.data?.count || 0;
    } catch (e) {
        console.error(e);
    }
};

const removeFromList = (itemId) => {
    list.value = list.value.filter((item) => item.id != itemId);
    totalCount.value = totalCount.value - 1;
    if (!list.value.length) {
        loadData();
    }
};

const userAdded = (user) => {
    list.value.unshift(user);
    totalCount.value = totalCount.value + 1;

    emit('added', user);
};

const userUpdated = (updatedUser) => {
    list.value = list.value.map((user) => {
        if (user.id === updatedUser.id) {
            return {
                ...updatedUser,
            };
        }

        return user;
    });

    emit('updated', updatedUser);
};

onMounted(() => {
    resetFilters();
});

defineExpose({
    loadData,
});
</script>
